// DEVN3-627: regras de tela das API Keys. As validações repetem as do backend (ApiKeyService /
// ApiKeyDominioNormalizador, DEVN3-626) só para avisar antes de enviar — o backend continua
// sendo quem decide.

export const DIAS_AVISO_EXPIRACAO = 15;
export const DIAS_VALIDADE_PADRAO = 90;
export const MAX_DOMINIOS = 10;
export const TAMANHO_MAXIMO_NOME = 100;

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Mensagem de erro do domínio, ou null quando é uma origem https válida (https://host[:porta]). */
export function validarDominio(texto: string): string | null {
  const valor = (texto ?? '').trim();
  if (!valor) return 'Informe o domínio.';
  if (valor.includes('*')) return 'Curinga não é permitido.';

  let url: URL;
  try {
    url = new URL(valor);
  } catch {
    return 'Use o formato https://host.';
  }

  if (url.protocol !== 'https:') return 'Somente https é permitido.';
  if (url.username || url.password) return 'Não pode conter usuário ou senha.';
  if (url.pathname !== '/' || url.search || url.hash) return 'Informe só a origem, sem caminho, query ou fragmento.';
  return null;
}

/** Origem como o navegador envia no cabeçalho Origin: minúscula, sem a porta 443. */
export function normalizarDominio(texto: string): string {
  return new URL(texto.trim()).origin;
}

/**
 * Datas da API vêm em UTC; as lidas do banco chegam sem sufixo de fuso ("2026-11-03T23:07:48")
 * e o Date do navegador as trataria como hora local. Sem fuso explícito, assume UTC.
 */
export function parseDataApi(valor: string): Date {
  const temFuso = /(Z|[+-]\d{2}:?\d{2})$/i.test(valor);
  return new Date(temFuso ? valor : `${valor}Z`);
}

/**
 * Dias inteiros que ainda faltam para expirar (arredonda para baixo): a chave vale até o fim do
 * dia escolhido, então hoje 06/10 com validade 16/10 são 10 dias, não 11. 0 = expira hoje;
 * negativo = já expirou.
 */
export function diasParaExpirar(expiraEm: string, agora: Date = new Date()): number {
  return Math.floor((parseDataApi(expiraEm).getTime() - agora.getTime()) / MS_POR_DIA);
}

/** Texto do aviso de vencimento próximo, ou null quando ainda falta mais que o limite. */
export function avisoExpiracao(expiraEm: string, agora: Date = new Date()): string | null {
  const dias = diasParaExpirar(expiraEm, agora);
  if (dias > DIAS_AVISO_EXPIRACAO) return null;
  if (dias <= 0) return 'expira hoje';
  return dias === 1 ? 'expira em 1 dia' : `expira em ${dias} dias`;
}

function paraInputData(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

function somarDias(base: Date, dias: number): Date {
  const data = new Date(base);
  data.setDate(data.getDate() + dias);
  return data;
}

/** Valores (YYYY-MM-DD, data local) para o input de validade. */
export function validadeMinima(agora: Date = new Date()): string {
  return paraInputData(somarDias(agora, 1));
}

export function validadePadrao(agora: Date = new Date()): string {
  return paraInputData(somarDias(agora, DIAS_VALIDADE_PADRAO));
}

/**
 * Último dia aceito: a chave vale até o fim do dia escolhido e o backend recusa acima de 1 ano,
 * então o teto é 1 ano menos 1 dia.
 */
export function validadeMaxima(agora: Date = new Date()): string {
  const umAno = new Date(agora);
  umAno.setFullYear(umAno.getFullYear() + 1);
  return paraInputData(somarDias(umAno, -1));
}

/** A chave vale até o fim do dia local escolhido; enviado em UTC. */
export function expiraEmIso(dataYYYYMMDD: string): string {
  const [ano, mes, dia] = dataYYYYMMDD.split('-').map(Number);
  return new Date(ano, mes - 1, dia, 23, 59, 59).toISOString();
}

// DEVN3-631: webhook. O backend (WebhookUrlValidador) também recusa endereços internos e
// resolve o DNS; aqui é só o aviso de formato antes de enviar.
export const TAMANHO_MAXIMO_URL_WEBHOOK = 500;

/** Mensagem de erro da URL do webhook, ou null quando o formato é aceitável. */
export function validarUrlWebhook(texto: string): string | null {
  const valor = (texto ?? '').trim();
  if (!valor) return 'Informe a URL.';
  if (valor.length > TAMANHO_MAXIMO_URL_WEBHOOK) return `Máximo de ${TAMANHO_MAXIMO_URL_WEBHOOK} caracteres.`;

  let url: URL;
  try {
    url = new URL(valor);
  } catch {
    return 'Use o formato https://host/caminho.';
  }

  if (url.protocol !== 'https:') return 'Somente https é permitido.';
  if (url.username || url.password) return 'Não pode conter usuário ou senha.';
  return null;
}

export const ROTULO_SITUACAO_EVENTO: Record<string, { texto: string; classe: string }> = {
  pendente: { texto: 'Pendente', classe: 'badge-light-warning' },
  entregue: { texto: 'Entregue', classe: 'badge-light-success' },
  falhou: { texto: 'Falhou', classe: 'badge-light-danger' },
  cancelado: { texto: 'Cancelado', classe: 'badge-light-dark' }
};

// DEVN3-636: tela de logs de integração.
export const DIAS_PERIODO_PADRAO = 7;
export const DIAS_PERIODO_MAXIMO = 90;

/** Entrega que falhou ou foi cancelada pode ser reenviada à mão. */
export function podeReenviar(situacao: string): boolean {
  return situacao === 'falhou' || situacao === 'cancelado';
}

/** Período padrão da tela (últimos 7 dias, hoje incluído), em YYYY-MM-DD local. */
export function periodoPadrao(agora: Date = new Date()): { de: string; ate: string } {
  return { de: paraInputData(somarDias(agora, -(DIAS_PERIODO_PADRAO - 1))), ate: paraInputData(agora) };
}

/** Mensagem de erro do período, ou null quando válido (mesmas regras do backend). */
export function validarPeriodo(de: string, ate: string): string | null {
  if (!de || !ate) return 'Informe o período.';
  const inicio = new Date(`${de}T00:00:00`);
  const fim = new Date(`${ate}T00:00:00`);
  if (inicio > fim) return 'A data inicial não pode ser depois da final.';
  const dias = Math.round((fim.getTime() - inicio.getTime()) / MS_POR_DIA) + 1;
  if (dias > DIAS_PERIODO_MAXIMO) return `O período pode ter no máximo ${DIAS_PERIODO_MAXIMO} dias.`;
  return null;
}

export const ROTULO_SITUACAO_OPERACAO: Record<string, { texto: string; classe: string }> = {
  pending: { texto: 'Pendente', classe: 'badge-light-warning' },
  processing: { texto: 'Processando', classe: 'badge-light-primary' },
  succeeded: { texto: 'Concluída', classe: 'badge-light-success' },
  failed: { texto: 'Falhou', classe: 'badge-light-danger' }
};

export const ROTULO_TIPO_OPERACAO: Record<string, string> = {
  'account-plan': 'Plano de contas',
  'account-entry': 'Lançamentos',
  'import-finish': 'Finalizar importação'
};

/** Classe do badge do status HTTP por faixa. */
export function classeStatusHttp(status: number): string {
  if (status >= 500) return 'badge-light-danger';
  if (status >= 400) return 'badge-light-warning';
  return 'badge-light-success';
}

export function formatarData(valor?: string | null): string {
  return valor ? parseDataApi(valor).toLocaleDateString('pt-BR') : '—';
}

export function formatarDataHora(valor?: string | null): string {
  return valor
    ? parseDataApi(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' } as Intl.DateTimeFormatOptions)
    : '—';
}
